import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  RiMailLine,
  RiPhoneLine,
  RiLockLine,
  RiShieldCheckLine,
  RiBuildingLine,
  RiPaletteLine,
  RiFileTextLine,
  RiTeamLine,
  RiBankLine,
} from '@remixicon/react';
import { apiClient } from '@/lib/api';
import { Skeleton } from '@components/ui/Skeleton';
import { useAuthStore } from '@store/authStore';
import { useAppStore } from '@store/appStore';
import { ROLES, normalizeRole } from '@/lib/permissions';
import {
  SettingsSection,
  SettingsRow,
  RoleBadge,
  UpdatePhoneModal,
  ChangePasswordModal,
  EditOrganizationDrawer,
  UpdateBrandingDrawer,
  StandardContractModal,
  TwoFactorAuthModal,
  TeamMembersPanel,
  AddMemberDrawer,
  RolePermissionsModal,
} from '../components';
import { initials, formatPhone } from '../utils/format';
import { encodeData, resolveAssetUrl } from '@/utils';
import { buildTeamInviteEmailHtml } from '@/utils/emailTemplates';
import { toast } from 'sonner';

const SkeletonRow = () => (
  <div className="flex items-center gap-4 px-4 py-3.5">
    <Skeleton className="w-5 h-5 rounded" />
    <div className="flex-1 space-y-1.5">
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-3 w-1/3" />
    </div>
    <Skeleton className="w-4 h-4 rounded" />
  </div>
);

const AccountSettings = () => {
  const queryClient = useQueryClient();
  const { userProfile, user, fetchUserProfile } = useAuthStore();
  const { setPageHeaderOverride, clearPageHeaderOverride } = useAppStore();
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [orgDrawerOpen, setOrgDrawerOpen] = useState(false);
  const [brandingDrawerOpen, setBrandingDrawerOpen] = useState(false);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [mfaModalOpen, setMfaModalOpen] = useState(false);
  const [teamViewOpen, setTeamViewOpen] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);

  const firstName = userProfile?.first_name || '';
  const lastName = userProfile?.last_name || '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || 'User';
  const email = userProfile?.email || user?.email || '';
  const phone = formatPhone(userProfile?.phone);
  const role = userProfile?.role;
  const normalizedRole = normalizeRole(role);
  const canAdmin = normalizedRole === ROLES.ADMIN || normalizedRole === ROLES.SUPER_ADMIN;
  const isAdmin = canAdmin;
  const hasPasswordLogin = !!user?.identities?.some((i) => i.provider === 'email');
  const oauthProvider = user?.identities?.find((i) => i.provider !== 'email')?.provider;
  const providerLabel = oauthProvider
    ? oauthProvider.charAt(0).toUpperCase() + oauthProvider.slice(1)
    : '';

  const { data: organization, isLoading: orgLoading } = useQuery({
    queryKey: ['organization', userProfile?.org_id],
    queryFn: () => apiClient.getOrganizationById(userProfile.org_id),
    enabled: !!userProfile?.org_id,
  });

  // Team members for the Manage Team Members panel — mirrors the mobile
  // InviteTeam screen: GET /users?org_id=... List the org's members so an admin
  // can see/manage them, not just the current user.
  const { data: orgMembers = [] } = useQuery({
    queryKey: ['orgMembers', userProfile?.org_id],
    queryFn: () => apiClient.getUsers({ org_id: userProfile.org_id, skip: 0, limit: 100 }),
    enabled: !!userProfile?.org_id,
    staleTime: 30 * 1000,
  });

  useEffect(() => {
    if (teamViewOpen) {
      setPageHeaderOverride({
        title: <span className="text-2xl font-bold text-white leading-tight">Team Members</span>,
        subtitle: organization?.name || '',
        onBack: () => setTeamViewOpen(false),
      });
    } else {
      setPageHeaderOverride({
        title: (
          <span className="flex items-center gap-4">
            <span className="w-14 h-14 rounded-full bg-amber-100 text-amber-900 flex items-center justify-center text-lg font-bold flex-shrink-0">
              {initials(firstName, lastName)}
            </span>
            <span className="flex flex-col">
              <span className="text-2xl font-bold text-white leading-tight">{fullName}</span>
              <span className="flex flex-wrap items-center gap-2 mt-1">
                {role && <RoleBadge>{role}</RoleBadge>}
              </span>
            </span>
          </span>
        ),
      });
    }
    return () => clearPageHeaderOverride();
  }, [setPageHeaderOverride, clearPageHeaderOverride, firstName, lastName, fullName, role, isAdmin, teamViewOpen, organization?.name]);

  const isOrgLoading = !!userProfile?.org_id && orgLoading;

  const branding = organization?.branding || {};
  const settings = organization?.settings || {};
  const accentColor = branding.color || branding.accent_color;
  const logoUrl = resolveAssetUrl(branding.logo);

  const organizationName = organization?.name || '';
  const organizationType = settings.type || 'Corporation';
  const teamMemberCount = organization?.member_count;
  const bankName = organization?.bank_name || '';
  const contractPath = branding.contract || '';
  const contractStatus = contractPath ? 'Uploaded' : 'Not uploaded';
  const contractUrl = resolveAssetUrl(contractPath);

  const handleSaveOrganization = async (payload) => {
    const orgId = userProfile?.org_id;
    if (!orgId) throw new Error('Organization not loaded');
    const updated = {
      ...(organization || {}),
      name: payload.name,
      phone: payload.phone,
      address: { ...(organization?.address || {}), street: payload.address },
      settings: {
        ...(organization?.settings || {}),
        pocTitle: payload.pocTitle,
        isSole: payload.soleProprietorship,
      },
    };
    await apiClient.updateOrganization(orgId, updated);
    await queryClient.invalidateQueries({ queryKey: ['organization', orgId] });
  };

  const handleSaveContract = async (payload) => {
    const orgId = userProfile?.org_id;
    if (!orgId) throw new Error('Organization not loaded');

    let contractPathNew = organization?.branding?.contract || '';
    const rawFile = payload.file?.raw;
    if (rawFile instanceof File) {

      // const renamed = new File(
      //   [rawFile],
      //   `${orgId}_${rawFile.name}`,
      //   { type: rawFile.type }
      // );
      // const uploaded = await apiClient.uploadFile(renamed, 'contract');

      // file info
      const file = payload.file?.raw;
      const key = `contracts/${orgId}_${file.name}`;

      // get s3 signed urls
      const s3Urls = await apiClient.getS3SignedUrl(key, file.type);
      
      // upload file to s3 and return public url
      contractPathNew = await apiClient.uploadFileToS3(file, s3Urls);

    } else if (!payload.file && !payload.existingUrl) {
      contractPathNew = '';
    }

    await apiClient.updateOrganization(orgId, {
      name: organization?.name,
      type: organization?.type,
      subscription_tier: organization?.subscription_tier,
      branding: {
        ...(organization?.branding || {}),
        contract: contractPathNew,
      },
    });
    await queryClient.invalidateQueries({ queryKey: ['organization', orgId] });
  };

  const handleUpdatePhone = async ({ countryCode, phone }) => {
    const profileId = userProfile?.userprofile_id;
    if (!profileId) throw new Error('User profile not loaded');
    const digits = (phone || '').replace(/\D/g, '');
    const fullPhone = digits ? `${countryCode}${digits}` : '';
    await apiClient.updateUser(profileId, { phone: fullPhone });
    await fetchUserProfile();
  };

  const handleUpdateMember = async (payload, original) => {
    const profileId = original?.userprofile_id;
    if (!profileId) {
      console.error('updateUser: missing userprofile_id on member', original);
      throw new Error('Member profile id is missing');
    }
    const body = {
      first_name: payload.firstName,
      last_name: payload.lastName,
      role: payload.role,
    };
    const result = await apiClient.updateUser(profileId, body);
    await fetchUserProfile();
    await queryClient.invalidateQueries({ queryKey: ['orgMembers', userProfile?.org_id] });
  };

  /**
   * Transition a member's status (Activate / Deactivate / Reactivate).
   * `status` and `is_active` move together so the existing AuthCallback /
   * SignIn gates (which read `status === 'ACTIVE'` and `is_active`) stay in
   * sync — matches the convention set in Welcome.jsx.
   */
  /**
   * Reassign every quote + client the removed user created over to the org
   * owner. Best-effort, paginated; a single failed row doesn't abort the
   * batch. Irreversible — there's no flag tracking the prior owner, so
   * restoring an archived user does NOT return their book.
   */
  const transferMemberRecords = async (fromUserprofileId, toUserprofileId) => {
    if (!fromUserprofileId || !toUserprofileId || fromUserprofileId === toUserprofileId) return;
    const LIMIT = 200;

    // Quotes
    try {
      let skip = 0;
      // Cap the loop hard so a buggy backend can't run away.
      for (let guard = 0; guard < 50; guard += 1) {
        const data = await apiClient.getQuotes(skip, LIMIT, { created_by: fromUserprofileId });
        const list = Array.isArray(data) ? data : (data?.data || []);
        if (!list.length) break;
        await Promise.all(list.map((q) => {
          const id = q.quote_id || q.id;
          if (!id) return null;
          return apiClient.updateQuote(id, { created_by: toUserprofileId }).catch((e) => {
            console.warn('transferMemberRecords: quote PATCH failed', id, e);
          });
        }));
        if (list.length < LIMIT) break;
        skip += LIMIT;
      }
    } catch (err) {
      console.warn('transferMemberRecords: quotes phase failed', err);
    }

    // Clients
    try {
      let skip = 0;
      for (let guard = 0; guard < 50; guard += 1) {
        const data = await apiClient.getClients(skip, LIMIT, { created_by: fromUserprofileId });
        const list = Array.isArray(data) ? data : (data?.data || []);
        if (!list.length) break;
        await Promise.all(list.map((c) => {
          const id = c.client_id || c.id;
          if (!id) return null;
          return apiClient.updateClient(id, { created_by: toUserprofileId }).catch((e) => {
            console.warn('transferMemberRecords: client PATCH failed', id, e);
          });
        }));
        if (list.length < LIMIT) break;
        skip += LIMIT;
      }
    } catch (err) {
      console.warn('transferMemberRecords: clients phase failed', err);
    }
  };

  const handleUpdateMemberStatus = async (member, nextStatus) => {
    const profileId = member?.userprofile_id;
    if (!profileId) {
      console.error('updateMemberStatus: missing userprofile_id', member);
      throw new Error('Member profile id is missing');
    }
    const isActive = nextStatus === 'ACTIVE';
    // A removed (ARCHIVED) member is demoted to GUEST — they keep no
    // operational role in the org once they're out.
    const body = { status: nextStatus, is_active: isActive };
    if (nextStatus === 'ARCHIVED') body.role = 'guest';

    // Optimistic — flip the chip on the row immediately so the list reflects
    // the new status without waiting for the refetch round-trip.
    const queryKey = ['orgMembers', userProfile?.org_id];
    const prev = queryClient.getQueryData(queryKey);
    queryClient.setQueryData(queryKey, (old) => {
      if (!Array.isArray(old)) return old;
      return old.map((m) =>
        m.userprofile_id === profileId
          ? { ...m, ...body }
          : m
      );
    });

    try {
      await apiClient.updateUser(profileId, body);
    } catch (err) {
      // Roll back the optimistic chip so the UI matches reality.
      if (prev !== undefined) queryClient.setQueryData(queryKey, prev);
      throw err;
    }

    // On Remove (ARCHIVED), reassign the user's quotes + clients to the org
    // owner so they don't dangle. NOTE: this is one-way; there's no record
    // of the prior owner, so a future Restore can't move them back.
    if (nextStatus === 'ARCHIVED') {
      const ownerId = organization?.owned_by;
      if (ownerId && ownerId !== profileId) {
        await transferMemberRecords(profileId, ownerId);
        // Refresh any view that lists quotes / clients by owner.
        queryClient.invalidateQueries({ queryKey: ['quotes'] });
        queryClient.invalidateQueries({ queryKey: ['clients'] });
      } else if (!ownerId) {
        console.warn('handleUpdateMemberStatus: organization.owned_by not set — skipping records transfer');
      }
    }

    await queryClient.invalidateQueries({ queryKey });
    toast.success(
      nextStatus === 'ARCHIVED' ? 'Member removed — quotes & clients transferred to owner'
      : nextStatus === 'INACTIVE' ? 'Member deactivated'
      : 'Member activated',
      { position: 'bottom-right' }
    );
    setAddMemberOpen(false);
    setEditingMember(null);
  };

  const handleInviteMember = async (payload) => {
    const orgId = userProfile?.org_id;
    if (!orgId) throw new Error('Organization not loaded');

    const companyName = organization?.name || '-';
    const inviterName =
      [userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ') || 'A team admin';

    const encoded = encodeData({
      firstName: payload.firstName,
      lastName: payload.lastName,
      designation: payload.role,
      role: payload.role,
      email: payload.email,
      org_id: orgId,
      company: companyName,
    });
    const registrationUrl = `${window.location.origin}/register/team?d=${encodeURIComponent(encoded)}`;

    await apiClient.sendMessage({
      From: 'tools@trustjet.ai',
      To: payload.email,
      Cc: organization?.billing_email || '',
      ReplyTo: organization?.billing_email || '',
      Subject: `You've been invited to join ${companyName}`,
      HtmlBody: buildTeamInviteEmailHtml({
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
        designation: payload.role,
        companyName,
        inviterName,
        registrationUrl,
        organization,
      }),
    });
  };

  const handleSaveBranding = async (payload) => {
    const orgId = userProfile?.org_id;
    if (!orgId) throw new Error('Organization not loaded');

    let logoPath = organization?.branding?.logo || '';
    if (payload.logoFile instanceof File) {

      // const renamed = new File(
      //   [payload.logoFile],
      //   `${orgId}_${payload.logoFile.name}`,
      //   { type: payload.logoFile.type }
      // );
      // const uploaded = await apiClient.uploadFile(renamed, 'logo');

      // file info
      const file = payload.logoFile;
      const key = `logo/images/${orgId}_${file.name}`;

      // get s3 signed urls
      const s3Urls = await apiClient.getS3SignedUrl(key, file.type);

      // upload file to s3 and return public url
      logoPath = await apiClient.uploadFileToS3(file, s3Urls);
      
    } else if (!payload.logoFile && !payload.logoPreview) {
      logoPath = '';
    }

    await apiClient.updateOrganization(orgId, {
      name: organization?.name,
      type: organization?.type,
      subscription_tier: organization?.subscription_tier,
      branding: {
        ...(organization?.branding || {}),
        color: payload.color,
        custom_colors: payload.customColors,
        logo: logoPath,
      },
    });
    await queryClient.invalidateQueries({ queryKey: ['organization', orgId] });
  };

  // Build the team-members list: API rows when available, otherwise just the
  // current user (mirrors mobile's `sortMembers` — put the signed-in user
  // first, then the rest, deduped by id/email).
  const currentMember = userProfile
    ? {
        id: userProfile.user_id || userProfile.id || user?.id,
        userprofile_id: userProfile.userprofile_id,
        first_name: firstName,
        last_name: lastName,
        email,
        role: role || (isAdmin ? 'admin' : ''),
      }
    : null;

  const teamMembers = (() => {
    // Archived members are removed from the team list — they live on in the DB
    // but no longer count as part of the active org. (To restore, contact support.)
    const isArchived = (m) => (m?.status || '').toUpperCase() === 'ARCHIVED';

    if (!Array.isArray(orgMembers) || orgMembers.length === 0) {
      return currentMember ? [currentMember] : [];
    }
    const currentId = currentMember?.id;
    const currentEmail = (currentMember?.email || '').toLowerCase();
    const isCurrent = (m) => {
      const mid = m.user_id || m.id;
      const mem = (m.email || '').toLowerCase();
      return (currentId && mid === currentId) || (currentEmail && mem && mem === currentEmail);
    };
    const rest = orgMembers.filter((m) => !isCurrent(m) && !isArchived(m));
    return currentMember ? [currentMember, ...rest] : orgMembers.filter((m) => !isArchived(m));
  })();

  return (
    <div className="max-w-3xl pb-8">
      <div className="overflow-hidden">
        <div
          className="flex items-start w-[200%] transition-transform duration-300 ease-out"
          style={{ transform: teamViewOpen ? 'translateX(-50%)' : 'translateX(0)' }}
        >
          <div className="w-1/2 flex-shrink-0">
            <div className="bg-white/70 backdrop-blur-sm rounded-[2.5rem] p-4 md:p-6 space-y-6">
              <SettingsSection label="Account Settings">
                <SettingsRow
                  icon={RiMailLine}
                  title={email || 'Add email address'}
                  subtitle={email ? 'Verified email address' : undefined}
                  hideChevron
                />
                <SettingsRow
                  icon={RiPhoneLine}
                  title={phone || 'Add phone number'}
                  subtitle={phone ? 'Verified phone number' : undefined}
                  onClick={() => setPhoneModalOpen(true)}
                />
                <SettingsRow
                  icon={RiLockLine}
                  title="Change password"
                  subtitle={
                    hasPasswordLogin
                      ? 'Last changed 2 months ago'
                      : `Managed by ${providerLabel || 'your sign-in provider'} — no password to change`
                  }
                  onClick={hasPasswordLogin ? () => setPasswordModalOpen(true) : undefined}
                  disabled={!hasPasswordLogin}
                />
                <SettingsRow
                  icon={RiShieldCheckLine}
                  title="Two-factor authentication"
                  subtitle="One-time passcode"
                  onClick={() => setMfaModalOpen(true)}
                />
              </SettingsSection>

              {canAdmin && (
                <>
                <SettingsSection label="Organization">
                  {isOrgLoading ? (
                    <>
                      <SkeletonRow />
                      <SkeletonRow />
                      <SkeletonRow />
                      <SkeletonRow />
                    </>
                  ) : (
                    <>
                      <SettingsRow
                        icon={RiBuildingLine}
                        title={organizationName || 'Organization'}
                        subtitle={organizationType || 'Corporation'}
                        onClick={() => setOrgDrawerOpen(true)}
                      />
                      <SettingsRow
                        icon={RiPaletteLine}
                        title="Update branding"
                        subtitle={accentColor ? `Logo, Accent ${accentColor}` : 'Logo, Accent color'}
                        onClick={() => setBrandingDrawerOpen(true)}
                      />
                      <SettingsRow
                        icon={RiFileTextLine}
                        title="Standard charter contract"
                        subtitle={contractStatus}
                        onClick={() => setContractModalOpen(true)}
                      />
                      <SettingsRow
                        icon={RiTeamLine}
                        title="Manage team members"
                        subtitle={
                          typeof teamMemberCount === 'number'
                            ? `${teamMemberCount} member${teamMemberCount === 1 ? '' : 's'}`
                            : 'Invite and manage your team'
                        }
                        onClick={() => setTeamViewOpen(true)}
                      />
                    </>
                  )}
                </SettingsSection>
                <SettingsSection label="Banking">
                {isOrgLoading ? (
                  <SkeletonRow />
                ) : (
                  <SettingsRow
                    icon={RiBankLine}
                    title="Update banking details"
                    subtitle={bankName || 'Add your bank account'}
                    onClick={() =>
                      toast.info('This section is under development', { position: 'bottom-right' })
                    }
                  />
                )}
              </SettingsSection>
              </>
              )}

              
            </div>
          </div>
          <div className="w-1/2 flex-shrink-0">
            {canAdmin && (
              <TeamMembersPanel
                members={teamMembers}
                currentUserId={userProfile?.user_id || userProfile?.id || user?.id}
                currentUserprofileId={userProfile?.userprofile_id}
                ownedBy={organization?.owned_by}
                onAddMember={() => {
                  setEditingMember(null);
                  setAddMemberOpen(true);
                }}
                onEditMember={(member) => {
                  setEditingMember({
                    id: member.id,
                    userprofile_id: member.userprofile_id,
                    firstName: member.first_name || '',
                    lastName: member.last_name || '',
                    email: member.email || '',
                    role: member.role || '',
                    // Status + is_active flow through to the drawer so the
                    // Activate / Deactivate / Reactivate action can be derived.
                    status: member.status || '',
                    is_active: member.is_active,
                  });
                  setAddMemberOpen(true);
                }}
              />
            )}
          </div>
        </div>
      </div>

      <UpdatePhoneModal
        open={phoneModalOpen}
        onClose={() => setPhoneModalOpen(false)}
        initialPhone={userProfile?.phone || ''}
        onSubmit={handleUpdatePhone}
      />

      <ChangePasswordModal
        open={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
      />

      <EditOrganizationDrawer
        open={orgDrawerOpen}
        onClose={() => setOrgDrawerOpen(false)}
        initialValues={{
          name: organizationName,
          address: organization?.address?.street || '',
          phone: organization?.phone || userProfile?.phone || '',
          pocTitle: settings.pocTitle || '',
          soleProprietorship: !!settings.isSole,
        }}
        onSubmit={handleSaveOrganization}
      />

      <UpdateBrandingDrawer
        open={brandingDrawerOpen}
        onClose={() => setBrandingDrawerOpen(false)}
        initialValues={{
          color: accentColor || '',
          logoPreview: logoUrl,
          customColors: branding.custom_colors || [],
        }}
        onSubmit={handleSaveBranding}
      />

      <StandardContractModal
        open={contractModalOpen}
        onClose={() => setContractModalOpen(false)}
        initialUrl={contractUrl}
        onSubmit={handleSaveContract}
      />

      <TwoFactorAuthModal
        open={mfaModalOpen}
        onClose={() => setMfaModalOpen(false)}
      />

      <AddMemberDrawer
        open={addMemberOpen}
        onClose={() => {
          setAddMemberOpen(false);
          setEditingMember(null);
        }}
        initialValues={editingMember}
        currentUserprofileId={userProfile?.userprofile_id}
        ownedBy={organization?.owned_by}
        onViewPermissions={() => setPermissionsModalOpen(true)}
        onSubmit={(payload, original) =>
          original ? handleUpdateMember(payload, original) : handleInviteMember(payload)
        }
        onChangeStatus={(nextStatus) =>
          editingMember ? handleUpdateMemberStatus(editingMember, nextStatus) : Promise.resolve()
        }
      />

      <RolePermissionsModal
        open={permissionsModalOpen}
        onClose={() => setPermissionsModalOpen(false)}
      />
    </div>
  );
};

export default AccountSettings;
